import { SearchCollectionName, SearchResult } from 'api/search';
import { ContactType } from 'constants/contacts';
import { capitalize } from 'lodash';
import {
    Campaign,
    Contact,
    HelpCenterArticle,
    Ticket,
    Transaction,
    TransactionPlan,
} from 'types/domain';

const DEFAULT_RESULT = {
    found: 0,
    hits: [],
    out_of: 0,
    page: 0,
    request_params: {},
    search_cutoff: false,
    search_time_ms: 0,
};

const findResultsByCollectionName = (result: SearchResult, collection: SearchCollectionName) =>
    result.request_params.collection_name.includes(collection);

const findResultByContactType = (results: SearchResult<Contact>[], contactType: ContactType) =>
    results.find(r => r.hits.at(0)?.document.type === contactType) || DEFAULT_RESULT;

const partitionResultsDataByResource = (results: SearchResult[]) => {
    const transactions = results.find(r =>
        findResultsByCollectionName(r, 'transactions'),
    ) as SearchResult<Omit<Transaction, 'line_items' | 'transactions'>>;
    const tickets = results.find(r =>
        findResultsByCollectionName(r, 'tickets'),
    ) as SearchResult<Ticket>;
    const transaction_plans = results.find(r =>
        findResultsByCollectionName(r, 'transaction_plans'),
    ) as SearchResult<TransactionPlan>;
    const campaigns = results.find(r =>
        findResultsByCollectionName(r, 'campaigns'),
    ) as SearchResult<Campaign>;
    const help_center = results.find(r =>
        findResultsByCollectionName(r, 'help_center'),
    ) as SearchResult<HelpCenterArticle>;

    /*
        Typesense doesn't give us enough information in the `result.request_params` to accurately
        determine the type of contact we're getting based on the filters applied, so we have to
        gather all results that are of the contact collection_name, then split them after based on
        the `result.hit[0].document.type`
    */
    const contactBasedResults = results.filter(r =>
        findResultsByCollectionName(r, 'contacts'),
    ) as SearchResult<Contact>[];
    const contacts = findResultByContactType(contactBasedResults, ContactType.Individual);
    const companies = findResultByContactType(contactBasedResults, ContactType.Company);

    return {
        contacts,
        companies,
        campaigns,
        transactions,
        tickets,
        transaction_plans,
        help_center,
    };
};

const transformSearchResults = (results: SearchResult[]) => {
    const partitionedResults = partitionResultsDataByResource(sanitizeNotFoundCollections(results));
    const noSearchResults = Object.values(partitionedResults).every(result => {
        return !result || result.found === 0;
    });

    return {
        ...partitionedResults,
        noSearchResults,
    };
};

const sanitizeNotFoundCollections = (results: SearchResult[]): SearchResult[] =>
    results.filter(r => !r.code);

const collectionNameToReadable = (collectionName: SearchCollectionName) => {
    switch (collectionName) {
        case 'transaction_plans':
            return 'Recurring plans';
        case 'help_center':
            return 'Help Center';
        case 'contacts':
        case 'transactions':
        case 'tickets':
        case 'campaigns':
        default:
            return capitalize(collectionName);
    }
};

export { partitionResultsDataByResource, transformSearchResults, collectionNameToReadable };
